<template>
  <div class="bz-selector-page report">
    <report-upsell-modal v-if="modalOpen" :kind="kind" :industry="industry" :area="area"
      :sc="sc" :structure="structure" :timeline="timeline"
      @accept="onUpsellAccept" @decline="onUpsellDecline" />

    <div class="bz-pagehead">
      <div class="bz-pagehead-l">
        <h1>{{ cfg.title }}</h1>
        <span class="bz-family">Report</span>
        <beta-pill :product="productKey" />
      </div>
    </div>

    <div class="bzB rail">
      <div class="bzB-steps">
        <step-card :n="stepNum('inputs')" title="Your data" icon="table_chart"
          :is-open="open === stepNum('inputs')" @toggle="toggle(stepNum('inputs'))"
          :value="inputSet && inputSet.name">
          <inputs-step :selected="inputSet" :product="productKey" :preselect-id="preselectDataId"
            @select="onInputsSelect" />
        </step-card>

        <step-card :n="stepNum('industry')" title="Industry" icon="factory"
          :is-open="open === stepNum('industry')" @toggle="toggle(stepNum('industry'))"
          :value="industry && industry.name + ' [' + industry.code + ']'">
          <industry-step :product="cfg.areaProduct" :selected="industry" :favorites="favorites"
            :show-class-counts="cfg.areaProduct === 'IFP'"
            @select="onIndustrySelect" @toggle-fav="toggleFav" />
        </step-card>

        <step-card :n="stepNum('area')" title="Market Area" icon="public"
          :locked="solo" lock-note="All US (Sole Proprietorship)"
          :is-open="open === stepNum('area')" @toggle="toggle(stepNum('area'))"
          :value="area && area.label">
          <area-step v-if="industry" :product="cfg.areaProduct" :industry="industry" :value="area"
            @change="onAreaChange" />
          <div v-else class="bz-empty">Pick an industry first — each area then shows whether it has data for it.</div>
        </step-card>

        <step-card v-if="hasSc" :n="stepNum('sc')" title="Sales Class" icon="paid"
          :locked="solo" lock-note="Industrywide (Sole Proprietorship)"
          :is-open="open === stepNum('sc')" @toggle="toggle(stepNum('sc'))"
          :value="sc && scLabel(sc)">
          <sales-class-step v-if="industry" :industry="industry" :value="sc" @change="onScChange" />
          <div v-else class="bz-empty">Pick an industry first — offered classes depend on it.</div>
        </step-card>

        <step-card v-if="hasDefaults" :n="stepNum('defaults')" title="Structure &amp; Time Series" icon="tune" preset
          :is-open="open === stepNum('defaults')" @toggle="toggle(stepNum('defaults'))"
          :value="structureLabel + ' · ' + timelineLabel">
          <div class="bz-defaults wide">
            <label>Structure</label>
            <div class="bz-seg">
              <button type="button" :class="{ on: structure === 'industry_firms' }" @click="setStructure('industry_firms')">Industry Firms</button>
              <!-- SP hidden until 3_26 MF/SPIF data exists — see helpers/spifAvailability.js -->
              <button v-if="spSelectorEnabled" type="button" :class="{ on: structure === 'sole_proprietorship' }" @click="setStructure('sole_proprietorship')">Sole Proprietorship</button>
            </div>
            <label>Time Series</label>
            <div class="bz-seg">
              <button type="button" :class="{ on: timeline === '3yr' }" @click="setTimeline('3yr')">3 Years Profile</button>
              <button type="button" :class="{ on: timeline === '5yr' }" @click="setTimeline('5yr')">5 Years Profile</button>
            </div>
          </div>
          <div v-if="solo" class="bz-constraint">
            <i class="material-icons" style="font-size:14px;line-height:1;">info</i>
            Sole Proprietorship locks Market Area to All US and Sales Class to Industrywide.
          </div>
        </step-card>
      </div>

      <div class="bz-rail">
        <div class="bz-rail-h">Your report</div>
        <div class="bz-rail-list">
          <button v-for="row in railRows" :key="row.label" class="bz-rail-row" :class="{ filled: !!row.value }"
            type="button" @click="toggle(row.step)">
            <span class="bz-rail-k">{{ row.label }}</span>
            <span class="bz-rail-v">{{ row.value || 'Not selected' }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
        </div>
        <div class="bz-status" :class="status.cls">
          <i class="material-icons" style="font-size:16px;line-height:1;">{{ status.icon }}</i>
          <span v-html="status.text"></span>
        </div>
        <div class="bz-rail-foot">
          <div class="bz-cta-wrap">
            <button class="bz-btn primary" type="button" :disabled="!ready || ctaDone || submitting" @click="handleAccess">
              <template v-if="ctaDone">
                <i class="material-icons" style="font-size:16px;line-height:1;">check</i>Added — opening report…
              </template>
              <template v-else-if="submitting">Processing…</template>
              <template v-else>
                Access Now<i class="material-icons" style="font-size:16px;line-height:1;">arrow_forward</i>
              </template>
            </button>
            <span v-if="ctaDone && upsellAccepted" class="bz-cta-note">2 products added together.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// The four Report products — purple family, one guided page replacing the
// 3-step wizard. kind: 'financial' | 'market' | 'valuation' | 'company'.
// Access: create via the classic endpoints, PRE-ATTACH the selected input set
// (reportInputs.js — faithful ports of the classic payload builders), then
// accessProduct and route to the report.
import StepCard from './StepCard.vue';
import BetaPill from './BetaPill.vue';
import IndustryStep from './IndustryStep.vue';
import AreaStep from './AreaStep.vue';
import SalesClassStep from './SalesClassStep.vue';
import InputsStep from './InputsStep.vue';
import ReportUpsellModal from './ReportUpsellModal.vue';
import {
  SALES_BY_ID, areaCodeOf, fetchOfferedSalesClasses, queueLoad,
  createSearchReport, createStandaloneReport, createIfSearchProfile, createImrSearchProfile, withTimeout,
} from './model';
import { fetchMyDataYears, attachReportInputs } from './reportInputs';
import {
  trackSelectorViewed, trackSelectorCompleted, trackIndustrySelected, trackStepCompleted,
  trackReportInputsSelected, trackUpsellShown, trackUpsellAccepted, trackUpsellDismissed,
} from './analytics';
import './beta-selector.css';
import { SP_SELECTOR_ENABLED } from '@/helpers/spifAvailability';

const KINDS = {
  financial: { title: 'Financial Report', steps: ['inputs', 'industry', 'area', 'sc', 'defaults'], areaProduct: 'IFP', productKey: 'financial_report' },
  market: { title: 'Market Report', steps: ['inputs', 'industry', 'area'], areaProduct: 'IMP', productKey: 'market_report' },
  valuation: { title: 'Valuation Report', steps: ['inputs', 'industry', 'area', 'sc'], areaProduct: 'IFP', productKey: 'valuation_report' },
  company: { title: 'Company Report', steps: ['inputs', 'industry', 'area', 'sc'], areaProduct: 'IFP', productKey: 'company_report' },
};

export default {
  name: 'report-page',
  components: { StepCard, BetaPill, IndustryStep, AreaStep, SalesClassStep, InputsStep, ReportUpsellModal },
  props: {
    kind: { type: String, required: true },
  },
  data() {
    return {
      spSelectorEnabled: SP_SELECTOR_ENABLED,
      inputSet: null,
      industry: null,
      area: null,
      sc: null,
      structure: 'industry_firms',
      timeline: '5yr',
      favorites: [],
      open: 1,
      ctaDone: false,
      submitting: false,
      modalOpen: false,
      upsellAccepted: false,
      upsellDismissed: false,
      upsellBandsLoaded: false,
      upsellCovered: false,
    };
  },
  computed: {
    cfg() {
      return KINDS[this.kind];
    },
    productKey() {
      return this.cfg.productKey;
    },
    preselectDataId() {
      return this.$route.query['data-id'] || null;
    },
    hasSc() {
      return this.cfg.steps.indexOf('sc') !== -1;
    },
    hasDefaults() {
      return this.cfg.steps.indexOf('defaults') !== -1;
    },
    solo() {
      return this.hasDefaults && this.structure === 'sole_proprietorship';
    },
    structureLabel() {
      return this.structure === 'sole_proprietorship' ? 'Sole Proprietorship' : 'Industry Firms';
    },
    timelineLabel() {
      return this.timeline === '3yr' ? '3 Years Profile' : '5 Years Profile';
    },
    ready() {
      return !!(this.inputSet && this.industry && this.area && (!this.hasSc || this.sc));
    },
    status() {
      if (!this.inputSet) return { cls: 'todo', icon: 'info', text: 'Pick or create an input set — your numbers drive this report' };
      if (!this.industry) return { cls: 'todo', icon: 'info', text: 'Select an industry to benchmark against' };
      if (!this.area) return { cls: 'todo', icon: 'info', text: 'Select a market area — only areas with data are enabled' };
      if (this.hasSc && !this.sc) return { cls: 'todo', icon: 'info', text: 'Select a sales class — only offered classes are enabled' };
      return { cls: 'ok', icon: 'check_circle', text: '<b>Available now</b> · benchmarked against your “' + this.inputSet.name + '” inputs' };
    },
    railRows() {
      const rows = [
        { label: 'Your data', value: this.inputSet && this.inputSet.name, step: this.stepNum('inputs') },
        { label: 'Industry', value: this.industry && this.industry.name + ' [' + this.industry.code + ']', step: this.stepNum('industry') },
        { label: 'Area', value: this.area && this.area.label, step: this.stepNum('area') },
      ];
      if (this.hasSc) rows.push({ label: 'Sales Class', value: this.sc && this.scLabel(this.sc), step: this.stepNum('sc') });
      if (this.hasDefaults) rows.push({ label: 'Structure', value: this.structureLabel + ' · ' + (this.timeline === '3yr' ? '3 Years' : '5 Years'), step: this.stepNum('defaults') });
      return rows;
    },
  },
  created() {
    try {
      this.favorites = JSON.parse(localStorage.getItem(this.favKey()) || '[]');
    } catch (e) {
      this.favorites = [];
    }
  },
  mounted() {
    trackSelectorViewed(this.productKey, 'beta');
  },
  methods: {
    favKey() {
      const uid = this.$store.getters['user/user_id'] || 'anon';
      return 'bzSelectorFavs:' + uid;
    },
    scLabel(id) {
      return SALES_BY_ID[id] ? SALES_BY_ID[id].label : id;
    },
    stepNum(name) {
      return this.cfg.steps.indexOf(name) + 1;
    },
    toggle(n) {
      this.open = this.open === n ? 0 : n;
    },
    freshen() {
      this.ctaDone = false;
      this.upsellAccepted = false;
      this.upsellDismissed = false;
    },
    toggleFav(code) {
      const i = this.favorites.indexOf(code);
      if (i === -1) this.favorites.push(code);
      else this.favorites.splice(i, 1);
      localStorage.setItem(this.favKey(), JSON.stringify(this.favorites));
    },
    onInputsSelect({ set, method, silent }) {
      this.inputSet = set;
      if (!silent) {
        this.open = this.stepNum('industry');
        this.freshen();
        const modified = set.modified || set.updated_at || null;
        const ageDays = modified ? Math.max(0, Math.round((Date.now() - new Date(modified).getTime()) / 86400000)) : null;
        trackReportInputsSelected(this.productKey, method, ageDays);
      }
    },
    onIndustrySelect({ node, method }) {
      this.industry = node;
      this.area = null;
      this.sc = null;
      this.open = this.stepNum('area');
      this.freshen();
      trackIndustrySelected(this.productKey, 'beta', method);
      this.preloadUpsellCoverage();
    },
    onAreaChange(area) {
      this.area = area;
      this.open = this.hasSc ? this.stepNum('sc') : 0;
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'market_area');
    },
    onScChange(id) {
      this.sc = id;
      this.open = 0;
      this.ctaDone = false;
      trackStepCompleted(this.productKey, 'beta', 'sales_class');
    },
    setStructure(v) {
      this.structure = v;
      if (v === 'sole_proprietorship') {
        this.area = { kind: 'us', label: 'All US' };
        this.sc = '1';
      }
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'defaults');
    },
    setTimeline(v) {
      this.timeline = v;
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'defaults');
    },

    /* ---------- report → profile cross-sell ---------- */
    async preloadUpsellCoverage() {
      this.upsellCovered = false;
      if (!this.industry) return;
      try {
        if (this.kind === 'market') {
          this.upsellCovered = true; // IMP superset — any standard selection carries
        } else {
          const offered = await fetchOfferedSalesClasses(this.industry.code);
          this.upsellCovered = offered.length > 0;
        }
      } catch (e) {
        this.upsellCovered = false; // slow/failed check = no prompt
      }
    },
    upsellApplicable() {
      // No IFP cross-sell on the Financial Report — the IFP is a strict subset
      // of the IFR (nothing in it that isn't already in the report), so the
      // add-on prompt offers nothing new (reported by Galen).
      if (this.kind === 'financial') return false;
      const a = this.area;
      if (!this.industry || !a) return false;
      if (!(a.kind === 'us' || a.kind === 'state' || a.kind === 'metro')) return false;
      return this.upsellCovered;
    },
    onUpsellAccept() {
      this.upsellAccepted = true;
      this.modalOpen = false;
      trackUpsellAccepted('report_to_profile', this.sc);
      this.doAccess();
    },
    onUpsellDecline() {
      this.upsellDismissed = true;
      this.modalOpen = false;
      trackUpsellDismissed('report_to_profile');
      this.doAccess();
    },

    /* ---------- access ---------- */
    handleAccess() {
      if (!this.ready || this.submitting || this.ctaDone) return;
      if (this.upsellApplicable() && !this.upsellAccepted && !this.upsellDismissed) {
        this.modalOpen = true;
        trackUpsellShown('report_to_profile', 'modal');
        return;
      }
      this.doAccess();
    },
    async doAccess() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const areaCode = areaCodeOf(this.area);
        const years = this.timeline === '3yr' ? 3 : 5;
        let id;
        if (this.kind === 'financial') {
          id = await createSearchReport(this.solo ? 'spif' : 'if', {
            industryCode: this.industry.code, areaCode, salesClassId: this.sc, years,
          });
        } else if (this.kind === 'market') {
          id = await createSearchReport('imr', {
            industryCode: this.industry.code, areaCode,
            radius: this.area.kind === 'radius' ? this.area.radius : null,
            zips: this.area.kind === 'zip' ? this.area.zips : null,
          });
        } else {
          id = await createStandaloneReport(this.kind, {
            industryCode: this.industry.code, areaCode, salesClassId: this.sc,
          });
        }

        // PRE-ATTACH the selected input set to the created report — faithful
        // ports of the classic per-type /user/report/save payload builders.
        // A failure here surfaces as an error (the report exists but would
        // open without the user's numbers, which reads as data loss).
        if (this.inputSet) {
          const loaded = await withTimeout(fetchMyDataYears(this.inputSet.id), 45000, 'load inputs');
          await withTimeout(
            attachReportInputs(this.kind, id, loaded.years, { soleProp: this.solo }),
            60000, 'attach inputs');
        }

        // matching-profile upsell (never blocks the report)
        if (this.upsellAccepted) {
          try {
            let pid;
            if (this.kind === 'market') {
              pid = await createImrSearchProfile({ industryCode: this.industry.code, areaCode, radius: null });
            } else {
              pid = await createIfSearchProfile({
                industryCode: this.industry.code, areaCode,
                salesClassId: this.sc || '1', years: 5, soleProp: false,
              });
            }
            await this.$store.dispatch('cart/accessProduct', { product_id: pid, access_legacy: false });
          } catch (e) {
            /* non-blocking */
          }
        }

        // Profile-only warm-up: /queue/load only knows how to dispatch profile
        // loads and 500s on a report id. Reports load on their own about-data
        // page, so a failure here must never block the report (this was the
        // "Something went wrong" on Access Now — it fired across every report).
        try {
          await queueLoad(id, this.$store.getters['user/user_id']);
        } catch (e) { /* non-fatal */ }
        const res = await this.$store.dispatch('cart/accessProduct', { product_id: id, access_legacy: false });

        trackSelectorCompleted(this.productKey, 'beta');
        this.ctaDone = true;

        const dq = this.inputSet ? { 'data-id': String(this.inputSet.id) } : {};
        if (res.cart) {
          this.$router.push({ name: 'Cart', params: { id } });
        } else if (res.access || (res.error && res.errorCode === 'product_already_accessed')) {
          if (this.kind === 'financial') {
            const base = this.solo ? '/sole-proprietorship-finanical-report/' : '/finanical-report/';
            this.$router.push({ path: base + id + '/about-data', query: dq });
          } else if (this.kind === 'market') {
            this.$router.push({ path: '/market-report/' + id + '/market-rep-about-data', query: dq });
          } else if (this.kind === 'valuation') {
            this.$router.push({ name: 'VRAbouttheData', params: { id }, query: dq });
          } else {
            this.$router.push({ name: 'CRAbouttheData', params: { id }, query: dq });
          }
        }
      } catch (e) {
        this.ctaDone = false;
        // Daily cap: the global dialog already explained it; skip the generic alert.
        if (e && e.dailyLimit) return;
        this.$store.dispatch('alerts/error', 'Something went wrong creating this report. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
